/**
 * main.ts — the portal entry point (the only <script> the shell loads).
 * =====================================================================
 * Registers the Web Components and starts the hash router. CSP-safe: this is the
 * sole bundled module; there is no inline JS anywhere in the portal.
 *
 * Routes (hash):
 *   #/jobs                       active work-order pipeline      <mango-jobs>
 *   #/detail/<short_name>        offer / detail                  <mango-job-detail>
 *   #/brief/<short_name>         scheduled field brief           <mango-job-brief>
 *   #/onsite/<short_name>        onsite job complete             <mango-onsite>
 *   #/sow/<short_name>           SOW completion                  <mango-sow>
 *   #/finalize/<short_name>      finalize (FMV + 55% settlement)  <mango-finalize>
 *   #/completed                  completed work orders           <mango-completed>
 *   #/calendar                   calendar (placeholder)          <mango-calendar>
 *   #/settings[/<tab>]           account settings (tabs)         <mango-settings>
 */
import { registerMangoJobs } from "./components/mango-jobs";
import { registerMangoJobDetail } from "./components/mango-job-detail";
import { registerMangoJobBrief } from "./components/mango-job-brief";
import { registerMangoOnsite } from "./components/mango-onsite";
import { registerMangoSow } from "./components/mango-sow";
import { registerMangoFinalize } from "./components/mango-finalize";
import { registerMangoCompleted } from "./components/mango-completed";
import { registerMangoCalendar } from "./components/mango-calendar";
import { registerMangoSettings } from "./components/mango-settings";
import { Router, type RouteParams } from "./router";
import { initAuthGate } from "./auth-gate";
import { populateSidebar } from "./profile";
import { getProviderProfile } from "./api/client";

// Wire the branded sign-in / sign-out gate (shown by http.ts on any 401/403) and
// the sidebar Sign out link. One delegated listener; CSP-safe.
initAuthGate();

// Mobile nav drawer: close the off-canvas drawer after a navigation tap. The
// drawer is a CSS-only checkbox-hack (no JS to un-check itself), so on a phone
// it would stay open covering the screen you just navigated to. Un-check the
// toggle on any in-drawer hash-link click, with a hashchange backstop. CSP-safe
// (bundled module, no inline JS); a no-op on desktop where the toggle is hidden.
function closeNavDrawer(): void {
  const toggle = document.getElementById("nav-toggle") as HTMLInputElement | null;
  if (toggle?.checked) toggle.checked = false;
}
document.querySelector(".sidebar")?.addEventListener("click", (e) => {
  if ((e.target as HTMLElement).closest('a[href^="#/"]')) closeNavDrawer();
});
window.addEventListener("hashchange", closeNavDrawer);

// Sidebar identity (company / role / Preferred badge) is populated in the boot
// gate at the bottom of this file — after the auth check confirms a provider.

registerMangoJobs();
registerMangoJobDetail();
registerMangoJobBrief();
registerMangoOnsite();
registerMangoSow();
registerMangoFinalize();
registerMangoCompleted();
registerMangoCalendar();
registerMangoSettings();

const outlet = document.getElementById("view-outlet");

/** Mount a custom element into the outlet, optionally stamping attributes. */
function mount(tag: string, attrs: Record<string, string> = {}): void {
  if (!outlet) return;
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  outlet.replaceChildren(el);
}

/** Highlight the sidebar nav link for a top-level section. */
function setActiveNav(route: string): void {
  document.querySelectorAll<HTMLAnchorElement>(".nav-item[data-route]").forEach((a) => {
    a.classList.toggle("active", a.dataset.route === route);
  });
}

/** A detail screen keyed by the first path segment (the work-order short_name). */
function detailRoute(tag: string): (params: RouteParams) => void {
  return (params) => {
    const shortName = params.segments[0] ?? "";
    // Stamp BOTH data-short-name (the component reads it to fetch the work order)
    // AND data-id (the delegated wireActions handler resolves the action target
    // via the nearest [data-id] ancestor). Without data-id, every lifecycle button
    // on a detail screen — Confirm Pickup, Arrive On Site, Submit Onsite Job
    // Complete, Finalize — silently no-ops because the handler can't resolve which
    // work order to act on. The jobs LIST stamps data-id per card; detail screens
    // have a single work order, so we stamp it once on the mounted element here.
    mount(tag, { "data-short-name": shortName, "data-id": shortName });
    setActiveNav("jobs"); // all work-order screens live under the Work orders nav
  };
}

const router = new Router();

router
  .on("jobs", () => {
    mount("mango-jobs");
    setActiveNav("jobs");
  })
  .on("detail", detailRoute("mango-job-detail"))
  .on("brief", detailRoute("mango-job-brief"))
  .on("onsite", detailRoute("mango-onsite"))
  .on("sow", detailRoute("mango-sow"))
  .on("finalize", detailRoute("mango-finalize"))
  .on("completed", () => {
    mount("mango-completed");
    setActiveNav("completed");
  })
  .on("calendar", () => {
    mount("mango-calendar");
    setActiveNav("calendar");
  })
  .on("settings", (params) => {
    const tab = params.segments[0];
    mount("mango-settings", tab ? { "data-tab": tab } : {});
    setActiveNav("settings");
  });

// Default view = the jobs list.
router.setFallback(() => router.navigate("jobs"));

// ── Gate before first paint ──────────────────────────────────────────────
// Confirm an authenticated PROVIDER session BEFORE mounting any screen, so an
// unauthenticated user never sees the empty shell flash. getProviderProfile()
// 403s for "not logged in" OR "logged in but not a provider"; http.ts reveals
// the branded auth-gate in that case. Either way we drop the boot splash.
function dropBootSplash(): void {
  document.getElementById("boot-splash")?.remove();
}

void (async () => {
  try {
    await getProviderProfile();
  } catch {
    // http.ts already revealed the auth-gate (401/403) — leave the shell unmounted.
    dropBootSplash();
    return;
  }
  void populateSidebar();
  router.start();
  dropBootSplash();
})();
